import * as DS from '@aurorasolar/ds';

import { ApplianceId, ScenarioId } from '../engine';

// Custom 16×16 stroke icons in the DS line style, for appliances and game
// readouts the DS icon set doesn't cover. Stroke-only so they inherit color.
const stroke = {
  fill: 'none',
  stroke: 'currentcolor',
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  strokeWidth: 1.3,
};

export const IconApplianceDishwasher = DS.generateIcon(
  <g {...stroke}>
    <rect height="12" rx="1" width="11" x="2.5" y="2" />
    <line x1="2.5" x2="13.5" y1="5" y2="5" />
    <circle cx="8" cy="9.5" r="2.6" />
    <circle cx="4.8" cy="3.5" r="0.2" />
  </g>,
  'appliance-dishwasher',
  'Dishwasher'
);

export const IconApplianceLaundry = DS.generateIcon(
  <g {...stroke}>
    <rect height="12" rx="1" width="11" x="2.5" y="2" />
    <circle cx="8" cy="8.8" r="3.1" />
    <circle cx="8" cy="8.8" r="1.2" />
    <line x1="10.5" x2="12" y1="3.6" y2="3.6" />
  </g>,
  'appliance-laundry',
  'Washer and dryer'
);

export const IconApplianceOven = DS.generateIcon(
  <g {...stroke}>
    <rect height="12" rx="1" width="11" x="2.5" y="2" />
    <circle cx="5.4" cy="4" r="0.4" />
    <circle cx="8" cy="4" r="0.4" />
    <circle cx="10.6" cy="4" r="0.4" />
    <rect height="5.4" width="7" x="4.5" y="6.6" />
  </g>,
  'appliance-oven',
  'Oven and stove'
);

export const IconApplianceTv = DS.generateIcon(
  <g {...stroke}>
    <rect height="8" rx="1" width="13" x="1.5" y="2.5" />
    <line x1="8" x2="8" y1="10.5" y2="12.5" />
    <line x1="5" x2="11" y1="13.5" y2="13.5" />
  </g>,
  'appliance-tv',
  'TV and games'
);

export const IconApplianceWifi = DS.generateIcon(
  <g {...stroke}>
    <path d="M2 6.5a9 9 0 0 1 12 0" />
    <path d="M4.3 9a5.7 5.7 0 0 1 7.4 0" />
    <path d="M6.5 11.4a2.6 2.6 0 0 1 3 0" />
    <circle cx="8" cy="13.6" fill="currentcolor" r="0.8" />
  </g>,
  'appliance-wifi',
  'Wi-Fi'
);

export const IconMoodHappy = DS.generateIcon(
  <g {...stroke}>
    <circle cx="8" cy="8" r="6" />
    <path d="M5.4 9.4a3.4 3.4 0 0 0 5.2 0" />
    <circle cx="5.8" cy="6.4" fill="currentcolor" r="0.5" />
    <circle cx="10.2" cy="6.4" fill="currentcolor" r="0.5" />
  </g>,
  'mood-happy',
  'Happy mood'
);

export const IconMoodNeutral = DS.generateIcon(
  <g {...stroke}>
    <circle cx="8" cy="8" r="6" />
    <line x1="5.6" x2="10.4" y1="10.2" y2="10.2" />
    <circle cx="5.8" cy="6.4" fill="currentcolor" r="0.5" />
    <circle cx="10.2" cy="6.4" fill="currentcolor" r="0.5" />
  </g>,
  'mood-neutral',
  'Neutral mood'
);

export const IconMoodStrained = DS.generateIcon(
  <g {...stroke}>
    <circle cx="8" cy="8" r="6" />
    <path d="M5.4 11a3.4 3.4 0 0 1 5.2 0" />
    <circle cx="5.8" cy="6.4" fill="currentcolor" r="0.5" />
    <circle cx="10.2" cy="6.4" fill="currentcolor" r="0.5" />
  </g>,
  'mood-strained',
  'Strained mood'
);

export const IconMoodPanicked = DS.generateIcon(
  <g {...stroke}>
    <circle cx="8" cy="8" r="6" />
    <circle cx="8" cy="10.6" r="1.4" />
    <line x1="4.9" x2="6.7" y1="5.7" y2="6.6" />
    <line x1="11.1" x2="9.3" y1="5.7" y2="6.6" />
  </g>,
  'mood-panicked',
  'Panicked mood'
);

export const IconScenarioFreeze = DS.generateIcon(
  <g {...stroke}>
    <line x1="8" x2="8" y1="1.5" y2="14.5" />
    <line x1="2.4" x2="13.6" y1="4.8" y2="11.2" />
    <line x1="2.4" x2="13.6" y1="11.2" y2="4.8" />
    <path d="M6.2 2.8 8 4.4l1.8-1.6" />
    <path d="M6.2 13.2 8 11.6l1.8 1.6" />
  </g>,
  'scenario-freeze',
  'Deep freeze forecast'
);

export const IconScenarioSpring = DS.generateIcon(
  <g {...stroke}>
    <circle cx="8" cy="5.5" r="1.7" />
    <ellipse cx="8" cy="2.6" rx="1.3" ry="1.5" />
    <ellipse cx="5.2" cy="4.6" rx="1.4" ry="1.3" transform="rotate(-60 5.2 4.6)" />
    <ellipse cx="10.8" cy="4.6" rx="1.4" ry="1.3" transform="rotate(60 10.8 4.6)" />
    <path d="M8 7.2v6.3" />
    <path d="M8 11.5c-1.6 0-2.8-.9-3.2-2.3 1.8-.3 3 .6 3.2 2.3Z" />
  </g>,
  'scenario-spring',
  'Lovely spring day forecast'
);

export const IconScenarioStorm = DS.generateIcon(
  <g {...stroke}>
    <path d="M4.4 9.5a3 3 0 0 1-.1-6 3.8 3.8 0 0 1 7.4-.4 2.7 2.7 0 0 1 .4 6.3" />
    <path d="m8.6 8-2 3.2h2.6l-2 3.3" />
  </g>,
  'scenario-storm',
  'Summertime storms forecast'
);

export const IconThermometer = DS.generateIcon(
  <g {...stroke}>
    <path d="M6.6 9.7V3.2a1.4 1.4 0 0 1 2.8 0v6.5a3 3 0 1 1-2.8 0Z" />
    <circle cx="8" cy="12.2" fill="currentcolor" r="1.2" />
    <line x1="8" x2="8" y1="11" y2="6" />
  </g>,
  'thermometer',
  'Indoor temperature'
);

export const APPLIANCE_ICONS: Record<ApplianceId, DS.IIconComponent> = {
  [ApplianceId.DISHWASHER]: IconApplianceDishwasher,
  [ApplianceId.EV_CHARGER]: DS.IconEvCharger,
  [ApplianceId.FRIDGE]: DS.IconRefrigerator,
  [ApplianceId.HVAC]: DS.IconHeatpump,
  [ApplianceId.LAUNDRY]: IconApplianceLaundry,
  [ApplianceId.LIGHTS]: DS.IconLightbulb,
  [ApplianceId.OVEN]: IconApplianceOven,
  [ApplianceId.TV_CONSOLE]: IconApplianceTv,
  [ApplianceId.WATER_HEATER]: DS.IconWater,
  [ApplianceId.WIFI]: IconApplianceWifi,
};

export const SCENARIO_ICONS: Record<ScenarioId, DS.IIconComponent> = {
  [ScenarioId.DEEP_FREEZE]: IconScenarioFreeze,
  [ScenarioId.LOVELY_SPRING_DAY]: IconScenarioSpring,
  [ScenarioId.SUMMERTIME_STORMS]: IconScenarioStorm,
};

export const moodIcon = (mood: number): DS.IIconComponent => {
  if (mood >= 70) return IconMoodHappy;
  if (mood >= 45) return IconMoodNeutral;
  if (mood >= 20) return IconMoodStrained;
  return IconMoodPanicked;
};
